import React from "react";
import { Box, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Select from "@mui/material/Select";
import {
  StyledHelperText,
  StyledInputLabel,
  StyledSelect,
} from "./StyledSelect";

const SelectField = React.forwardRef(({
  label,
  options,
  value,
  onChange,
  placeholder,
  error,
  helperText,
  disabled,
  onBlur,
  required,
  id,
  colorOptions = [],
  objectValue = false,
}, ref) => {
  // For objectValue, normalize value to use same object reference as options
  const normalizedValue = React.useMemo(() => {
    if (objectValue && options && value) {
      // Find the matching option object (same reference) from options array
      const matchingOption = options?.find((opt) => opt.id === value.id);
      return matchingOption || value;
    }
    return value;
  }, [objectValue, options, value]);

  const handleChange = (event) => {
    if (objectValue) {
      // For objectValue, ensure we use the option object from options array
      const selectedValue = event.target.value;
      const matchingOption = options?.find((opt) => opt.id === selectedValue.id) || selectedValue;
      onChange?.({ target: { value: matchingOption } });
    } else {
      onChange?.(event);
    }
  };
  const renderOptions = () => {
    if (colorOptions?.length > 0 && !objectValue) {
      return colorOptions.map((val, idx) => (
        <MenuItem key={idx} value={val}>
          <Box
            sx={{
              backgroundColor: val.value,
              width: "20px",
              height: "20px",
              marginRight: "10px",
              display: "inline-block",
            }}
          />
          <span>{val.name}</span>
        </MenuItem>
      ));
    } else if (objectValue) {
      return options?.map((val, idx) => (
        <MenuItem key={idx} value={val}>
          {val.name}
        </MenuItem>
      ));
    } else if (options && !objectValue) {
      return options?.map((val, idx) => (
        <MenuItem key={idx} value={val}>
          {val}
        </MenuItem>
      ));
    }
  };
  return (
    <StyledSelect>
      <StyledInputLabel>
        {label}
        {required && <span className='required'> * </span>}
      </StyledInputLabel>
      <Select
        ref={ref}
        labelId='simple-selectlabel'
        id={id}
        disabled={disabled}
        autoComplete="off"
        displayEmpty
        fullWidth
        value={objectValue ? normalizedValue : value}
        onChange={objectValue ? handleChange : onChange}
        IconComponent={KeyboardArrowDownIcon}
        error={error}
        onBlur={onBlur}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 200,
              overflowY: "auto",
            },
          },
        }}
        renderValue={(selected) => {
          if (selected && colorOptions?.length > 0 && !objectValue) {
            return (
              <Box className='color-option-label'>
                <Box
                  sx={{
                    backgroundColor: selected?.value,
                    width: "18px",
                    height: "18px",
                    marginRight: "10px",
                    display: "inline-block",
                    border: "0.2px solid gray",
                  }}
                />
                {selected?.name}
              </Box>
            );
          } else if (selected && objectValue) {
            return <span className='truncate-text'>{selected?.name}</span>;
          } else if (selected?.length && !objectValue) {
            return <span className='truncate-text'>{selected}</span>;
          }
          return <span className='placeholder'>{placeholder}</span>;
        }}
      >
        {renderOptions()}
      </Select>
      <StyledHelperText>{helperText}</StyledHelperText>
    </StyledSelect>
  );
});

SelectField.displayName = "SelectField";

export default SelectField;
